import { fetchAssetLibrary, type AssetCollection, type AssetItem } from '../assets/api.js';
import { resolveMediaUrl } from '../mediaUrl.js';

export type AssetPickerOptions = {
  collection: AssetCollection;
  title: string;
  onSelect: (item: AssetItem) => void;
  onError?: (message: string) => void;
};

function closeDialog(dialog: HTMLDialogElement): void {
  dialog.close();
  dialog.remove();
}

/** Ouvre une modale pour choisir une image dans la bibliothèque d'assets. */
export function openAssetImagePicker(options: AssetPickerOptions): void {
  const dialog = document.createElement('dialog');
  dialog.className =
    options.collection === 'drivers'
      ? 'asset-picker-dialog asset-picker-dialog--drivers'
      : 'asset-picker-dialog';
  dialog.setAttribute('aria-labelledby', 'asset-picker-title');

  const inner = document.createElement('div');
  inner.className = 'asset-picker-dialog-inner';

  const head = document.createElement('div');
  head.className = 'asset-picker-head';
  head.innerHTML = `<h2 id="asset-picker-title" class="asset-picker-title">${options.title}</h2>`;

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'btn btn-ghost asset-picker-close';
  closeBtn.textContent = 'Fermer';
  closeBtn.addEventListener('click', () => closeDialog(dialog));
  head.appendChild(closeBtn);

  const search = document.createElement('input');
  search.type = 'search';
  search.className = 'asset-picker-search';
  search.placeholder = 'Rechercher un fichier…';
  search.setAttribute('aria-label', 'Filtrer les visuels');

  const grid = document.createElement('div');
  grid.className = 'asset-picker-grid';
  grid.setAttribute('role', 'listbox');
  grid.setAttribute('aria-label', 'Visuels disponibles');

  const status = document.createElement('p');
  status.className = 'asset-picker-status';
  status.textContent = 'Chargement…';

  inner.append(head, search, status, grid);
  dialog.appendChild(inner);
  document.body.appendChild(dialog);

  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) closeDialog(dialog);
  });
  dialog.addEventListener('cancel', () => closeDialog(dialog));

  let items: AssetItem[] = [];

  const renderGrid = (): void => {
    const q = search.value.trim().toLowerCase();
    const filtered = q
      ? items.filter(
          (it) => it.filename.toLowerCase().includes(q) || it.publicPath.toLowerCase().includes(q)
        )
      : items;
    grid.innerHTML = '';
    if (filtered.length === 0) {
      status.textContent = q ? 'Aucun visuel ne correspond.' : 'Bibliothèque vide.';
      status.hidden = false;
      return;
    }
    status.hidden = true;
    filtered.forEach((item) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'asset-picker-item';
      btn.setAttribute('role', 'option');
      btn.title = item.filename;
      const img = document.createElement('img');
      img.src = resolveMediaUrl(item.publicPath);
      img.alt = item.filename;
      img.loading = 'lazy';
      const cap = document.createElement('span');
      cap.className = 'asset-picker-item-name';
      cap.textContent = item.filename;
      btn.append(img, cap);
      btn.addEventListener('click', () => {
        options.onSelect(item);
        closeDialog(dialog);
      });
      grid.appendChild(btn);
    });
  };

  search.addEventListener('input', () => renderGrid());

  dialog.showModal();

  void fetchAssetLibrary()
    .then((lib) => {
      items = options.collection === 'drivers' ? lib.drivers : lib.cars;
      status.textContent = `${items.length} visuel(s)`;
      renderGrid();
    })
    .catch((err: Error) => {
      status.textContent = err.message || 'Impossible de charger la bibliothèque.';
      options.onError?.(status.textContent);
    });
}
